"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LocalMarkdownContentProvider = void 0;
exports.registerLocalPosts = registerLocalPosts;
exports.getLocalNotes = getLocalNotes;
exports.createLocalContentProvider = createLocalContentProvider;
const config_1 = require("../constants/config");
const markdown_1 = require("./markdown");
// Posts that carry this tag are grouped into a reconstructed series, since the
// Hashnode GitHub backup does not store series membership in frontmatter.
const SERIES_DEFS = [
    { tag: 'ouroboros', id: 'ouroboros', name: 'Ouroboros', slug: 'ouroboros' },
];
let registeredPosts = [];
let registeredNotes = [];
function registerLocalPosts(files) {
    registeredPosts = (0, markdown_1.parseMarkdownPosts)(files);
    registeredNotes = (0, markdown_1.parseNotes)(files);
}
function getLocalNotes() {
    return registeredNotes;
}
function seriesDefForPost(post) {
    const lowered = post.tags.map((t) => t.toLowerCase());
    return SERIES_DEFS.find((s) => lowered.includes(s.tag));
}
class LocalMarkdownContentProvider {
    all() {
        return registeredPosts.map((post) => {
            const def = seriesDefForPost(post);
            return def
                ? { ...post, series: { id: def.id, name: def.name, slug: def.slug } }
                : post;
        });
    }
    async getPosts(limit, offset = 0) {
        const posts = this.all();
        return limit ? posts.slice(offset, offset + limit) : posts.slice(offset);
    }
    async getPostBySlug(slug) {
        return this.all().find((p) => p.slug === slug) || null;
    }
    async getMetadata() {
        return {
            siteTitle: config_1.CONFIG.SITE_TITLE,
            siteDescription: config_1.CONFIG.SITE_DESCRIPTION,
            siteUrl: config_1.CONFIG.SITE_URL,
            authorName: config_1.CONFIG.AUTHOR_NAME,
            authorEmail: config_1.CONFIG.AUTHOR_EMAIL,
            socialLinks: {},
            theme: {
                primaryColor: '#2563eb',
                accentColor: '#3b82f6',
                backgroundColor: '#ffffff',
                textColor: '#111827',
            },
            navigation: [
                { label: 'Home', href: '/', external: false, order: 1 },
                { label: 'Posts', href: '/posts', external: false, order: 2 },
                { label: 'About', href: '/about', external: false, order: 3 },
            ],
            featuredPostsCount: config_1.CONFIG.FEATURED_POSTS_COUNT,
            postsPerPage: config_1.CONFIG.POSTS_PER_PAGE,
        };
    }
    async searchPosts(query) {
        const q = query.toLowerCase();
        return this.all().filter((p) => p.title.toLowerCase().includes(q) ||
            p.content.toLowerCase().includes(q) ||
            p.excerpt.toLowerCase().includes(q) ||
            p.tags.some((t) => t.toLowerCase().includes(q)));
    }
    async getFeaturedPosts() {
        const flagged = this.all().filter((p) => p.featured);
        const base = flagged.length > 0 ? flagged : this.all();
        return base.slice(0, config_1.CONFIG.FEATURED_POSTS_COUNT);
    }
    async getPostsByCategory(category) {
        return this.all().filter((p) => p.category === category);
    }
    async getPostsByTag(tag) {
        return this.all().filter((p) => p.tags.includes(tag));
    }
    async getSeriesPosts(seriesSlug) {
        return this.all()
            .filter((p) => p.series?.slug === seriesSlug)
            .sort((a, b) => a.publishedAt.getTime() - b.publishedAt.getTime());
    }
    async getTags() {
        const set = new Set();
        this.all().forEach((p) => p.tags.forEach((t) => set.add(t)));
        return Array.from(set).sort();
    }
    async getCategories() {
        const set = new Set();
        this.all().forEach((p) => set.add(p.category));
        return Array.from(set).sort();
    }
    async getSeries() {
        const posts = this.all();
        return SERIES_DEFS.map((def) => ({
            id: def.id,
            name: def.name,
            slug: def.slug,
            posts: posts
                .filter((p) => p.series?.slug === def.slug)
                .sort((a, b) => a.publishedAt.getTime() - b.publishedAt.getTime()),
        })).filter((s) => s.posts.length > 0);
    }
    async getDraft() {
        return null;
    }
}
exports.LocalMarkdownContentProvider = LocalMarkdownContentProvider;
function createLocalContentProvider() {
    return new LocalMarkdownContentProvider();
}
